import React from "react";
import {
	IconButton,
	Typography,
	Grid2 as Grid,
	TextField,
	Box,
	useMediaQuery,
	useTheme,
	InputAdornment,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditIcon from "@mui/icons-material/Edit";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { CalendarIcon } from "@mui/x-date-pickers/icons";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enAU } from "date-fns/locale";
import parseISO from "date-fns/parseISO";
import format from "date-fns/format";
import StatusLabel from "../StatusLabel/StatusLabel";
import { receiveStatus } from "../../util/receiveStatus";
import { isEditable } from "../../util/receiveUtil";
import HistoryIcon from "@mui/icons-material/History";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ErrorIcon from "@mui/icons-material/Error";
import ReceiveFailureAlert from "../ReceiveFailureAlert/ReceiveFailureAlert";
import { useStyles } from "./Receive.styles";
import { PreviousReceiveHeaderProps } from "./types";
import { DatePickerMobileActionBar } from "@/components/Common/DatePickerMobileActionBar";
import CurrencyTextField from "../CurrencyTextField/CurrencyTextField";
import { useFeatureCheck } from "../../hooks/useFeatureCheck";
import { AlertInformation } from "../AlertInformation/AlertInformation";
import { getFreightCostHighWarning, isFreightCostHigh } from "@/util/freightUtil";

function PreviousReceiveHeader(props: PreviousReceiveHeaderProps) {
	const { receive, onReceiveEditClick, onSetReceiveProperty, open, setOpen, onHistoryClick, validateInvoiceNumber } =
		props;
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const { isFeatureEnabled } = useFeatureCheck({
		locationId: receive.locationId,
		receiveType: receive.receiveType,
	});
	const showFreightCost = isFeatureEnabled("captureFreightCost");
	const freightCostHigh = showFreightCost && isFreightCostHigh(receive.totalInvoicedFreightEx, receive.totalCostEx);

	const handleInvoiceDateChange = (date) => {
		if (date && !isNaN(date.getTime())) {
			onSetReceiveProperty(receive.id, "invoiceDate", date.toISOString());
		}
	};

	const handleDeliveredDateChange = (date) => {
		if (date && !isNaN(date.getTime())) {
			onSetReceiveProperty(receive.id, "receivedDate", date.toISOString());
		}
	};

	const handleInvoiceNumberChange = (event) => {
		const invoiceNumber = event.target.value?.toUpperCase()?.replace(/\s/g, "");
		onSetReceiveProperty(receive.id, "invoiceNumber", invoiceNumber);
	};

	const handleFreightExChange = (_event: React.SyntheticEvent, value: number | string | null | undefined) => {
		const raw = value?.toString();
		onSetReceiveProperty(receive.id, "totalInvoicedFreightEx", raw == null || raw === "" ? undefined : raw);
	};
	const onInvoiceNumberTextFieldBlur = () => {
		validateInvoiceNumber && validateInvoiceNumber(receive.invoiceNumber);
	};
	const latestStatusHistory = receive.statusHistory?.length > 0 ? receive.statusHistory.at(-1) : null;

	// Treat 0001-01-01 (DateTime.MinValue from .NET) as null so the calendar defaults to today
	const isMinDate = (date: Date) => date.getFullYear() <= 1;

	const parsedInvoiceDate = (() => {
		if (!receive.invoiceDate) return null;
		if (receive.invoiceDate instanceof Date) return isMinDate(receive.invoiceDate) ? null : receive.invoiceDate;
		if (typeof receive.invoiceDate === "string") {
			const parsed = parseISO(receive.invoiceDate);
			if (isNaN(parsed.getTime()) || isMinDate(parsed)) return null;
			return parsed;
		}
		return null;
	})();

	const parsedReceivedDate = (() => {
		if (!receive.receivedDate) return null;
		if (receive.receivedDate instanceof Date) return isMinDate(receive.receivedDate) ? null : receive.receivedDate;
		if (typeof receive.receivedDate === "string") {
			const parsed = parseISO(receive.receivedDate);
			if (isNaN(parsed.getTime()) || isMinDate(parsed)) return null;
			return parsed;
		}
		return null;
	})();

	return (
		<Grid className={classes.offset}>
			<Grid container alignItems="center" className={classes.receiveHeaderRow}>
				<Grid size={4} sx={{ overflow: "hidden", minWidth: 0 }}>
					<Grid container justifyContent="center" alignItems="center">
						<Grid>
							<IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
								{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
							</IconButton>
						</Grid>
						<Grid size="grow" className={classes.previousReceiveHeaderContainer}>
							<Typography variant="subtitle2">Invoice</Typography>
						</Grid>
					</Grid>
					<Grid
						container
						direction="column"
						justifyContent="flex-end"
						className={classes.receiveHeaderRowValues}
						sx={{ overflow: "hidden" }}
					>
						{receive.status === receiveStatus.reviewing ? (
							<TextField
								id={`${receive.id}`}
								value={receive.invoiceNumber}
								onChange={handleInvoiceNumberChange}
								slotProps={{
									htmlInput: {
										style: { textAlign: "center", fontSize: 15 },
									},
								}}
								type="text"
								size="small"
								variant="standard"
								autoComplete="off"
								onBlur={onInvoiceNumberTextFieldBlur}
							/>
						) : (
							<Typography variant="body2" noWrap>
								{receive.invoiceNumber}
							</Typography>
						)}
					</Grid>
				</Grid>
				<Grid size={8} sx={{ overflow: "hidden", minWidth: 0 }}>
					<Grid container justifyContent="center" className={classes.previousReceiveHeaderContainer} columnSpacing={1}>
						<Grid size={3}>
							<Grid container justifyContent="center" direction="column">
								<Grid>
									<Typography variant="subtitle2">Ordered</Typography>
								</Grid>
								<Grid container direction="column" justifyContent="flex-end" className={classes.receiveHeaderRowValues}>
									<Typography variant="body2">{receive.totalOrderedQuantity}</Typography>
								</Grid>
							</Grid>
						</Grid>
						<Grid size={3}>
							<Grid container justifyContent="center" direction="column">
								<Grid>
									<Typography variant="subtitle2">Received</Typography>
								</Grid>
								<Grid container direction="column" justifyContent="flex-end" className={classes.receiveHeaderRowValues}>
									<Typography variant="body2">{receive.totalReceivedQuantity}</Typography>
								</Grid>
							</Grid>
						</Grid>
						<Grid size={4}>
							<Grid>
								<Typography variant="subtitle2">Status</Typography>
							</Grid>
							<Grid container direction="column" justifyContent="flex-end" className={classes.receiveHeaderRowValues}>
								<StatusLabel status={receive.status} size={isMobile ? "extraSmall" : "small"} />
							</Grid>
						</Grid>
						<Grid size={2} className={classes.editIcon}>
							{isEditable(receive) && (
								<IconButton
									aria-label="edit row"
									size="small"
									onClick={() => onReceiveEditClick(receive.invoiceNumber)}
								>
									<EditIcon sx={{ color: "text.primary" }} />
								</IconButton>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Grid container sx={{ maxWidth: "100%", overflow: "hidden" }}>
				<Grid size={12}>
					<Grid container size={12}>
						<Grid container size={12} direction="row">
							<Grid size={6}>
								<Typography variant="body2" display="inline">
									Invoiced Date
								</Typography>
							</Grid>
							<Grid size={6}>
								{receive.status === receiveStatus.reviewing ? (
									<Box display="flex" flexDirection="row">
										<Box>
											<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
												<DatePicker
													value={parsedInvoiceDate}
													onChange={handleInvoiceDateChange}
													maxDate={new Date()}
													closeOnSelect
													slots={{ actionBar: DatePickerMobileActionBar }}
													slotProps={{
														textField: {
															id: `invoice-date-${receive.id}`,
															size: "small",
															variant: "standard",
														},
														field: {
															InputProps: {
																endAdornment: (
																	<InputAdornment position="end">
																		<CalendarIcon fontSize="small" />
																	</InputAdornment>
																),
															},
														},
														actionBar: {
															actions: isMobile ? ["cancel", "accept"] : [],
															onSetDefaultDate: () => {
																if (!parsedInvoiceDate) {
																	onSetReceiveProperty(receive.id, "invoiceDate", new Date().toISOString());
																}
															},
														} as any,
													}}
												/>
											</LocalizationProvider>
										</Box>
									</Box>
								) : (
									<Typography variant="body2" display="inline">
										{receive.invoiceDate ? format(parseISO(receive.invoiceDate), "dd/MM/yyyy") : "N/A"}
									</Typography>
								)}
							</Grid>
						</Grid>
						<Grid container size={12} direction="row">
							<Grid size={6}>
								<Typography variant="body2" display="inline">
									Delivered Date
								</Typography>
							</Grid>
							<Grid size={6}>
								{receive.status === receiveStatus.reviewing ? (
									<Box display="flex" flexDirection="row">
										<Box>
											<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
												<DatePicker
													onChange={handleDeliveredDateChange}
													value={parsedReceivedDate}
													maxDate={new Date()}
													closeOnSelect
													slots={{ actionBar: DatePickerMobileActionBar }}
													slotProps={{
														textField: {
															id: `delivered-date-${receive.id}`,
															size: "small",
															variant: "standard",
														},
														field: {
															InputProps: {
																endAdornment: (
																	<InputAdornment position="end">
																		<CalendarIcon fontSize="small" />
																	</InputAdornment>
																),
															},
														},
														actionBar: {
															actions: isMobile ? ["cancel", "accept"] : [],
															onSetDefaultDate: () => {
																if (!parsedReceivedDate) {
																	onSetReceiveProperty(receive.id, "receivedDate", new Date().toISOString());
																}
															},
														} as any,
													}}
												/>
											</LocalizationProvider>
										</Box>
									</Box>
								) : (
									<Typography variant="body2" display="inline">
										{receive.receivedDate ? format(parseISO(receive.receivedDate), "dd/MM/yyyy") : "N/A"}
									</Typography>
								)}
							</Grid>
						</Grid>
						{showFreightCost && (
							<Grid container size={12} direction="row" alignItems="center">
								<Grid size={6}>
									<Box display="flex" alignItems="center">
										<Typography variant="body2" display="inline">
											Freight Cost Ex
										</Typography>
										{!freightCostHigh && (
											<LocalShippingIcon
												sx={{ color: theme.palette.custom.jbColours.darkOrange, fontSize: 20, marginLeft: 0.5 }}
											/>
										)}
									</Box>
								</Grid>
								<Grid size={6}>
									<Box display="flex" alignItems="center">
										{receive.status === receiveStatus.reviewing ? (
											<CurrencyTextField
												id={`freight-ex-${receive.id}`}
												size="small"
												variant="standard"
												value={receive.totalInvoicedFreightEx ?? ""}
												onChange={handleFreightExChange}
												currencySymbol="$"
												minimumValue="0"
												emptyInputBehavior="null"
											/>
										) : (
											<Typography
												variant="body2"
												display="inline"
												sx={freightCostHigh ? { color: theme.palette.custom.jbColours.darkOrange } : undefined}
											>
												${receive.totalInvoicedFreightEx?.toFixed(2) ?? (0.0).toFixed(2)}
											</Typography>
										)}
										{freightCostHigh && (
											<ErrorIcon
												sx={{ color: theme.palette.custom.jbColours.darkOrange, fontSize: 20, marginLeft: 0.5 }}
											/>
										)}
									</Box>
								</Grid>
							</Grid>
						)}
						<Grid container size={12} direction="row">
							<Grid size={6}>
								<Typography variant="body2" display="inline">
									Line Total Cost Ex
								</Typography>
							</Grid>
							<Grid size={6}>
								<Typography variant="body2" display="inline">
									${receive.totalCostEx?.toFixed(2)}
								</Typography>
							</Grid>
						</Grid>
						<Grid container size={12} direction="row" alignItems="center">
							<Grid size={6}>
								<Typography variant="body2" display="inline">
									Total Cost Inc
								</Typography>
								{showFreightCost && (
									<Typography color="textSecondary" sx={{ fontSize: "0.77rem" }}>
										(incl freight charges)
									</Typography>
								)}
							</Grid>
							<Grid size="grow">
								<Typography variant="body2" display="inline">
									${receive.totalCostInc?.toFixed(2)}
								</Typography>
							</Grid>
							<Grid>
								<IconButton aria-label="history" size="small" onClick={() => onHistoryClick(receive)}>
									<HistoryIcon />
								</IconButton>
							</Grid>
						</Grid>
						{freightCostHigh && (
							<Box style={{ marginTop: theme.spacing(1) }}>
								<AlertInformation severity="warning" message={getFreightCostHighWarning(receive.receiveType)} />
							</Box>
						)}
						<Grid size={12} sx={{ maxWidth: '100%' }}>
							<ReceiveFailureAlert
								status={latestStatusHistory?.status ?? receive.status}
								latestStatusHistory={latestStatusHistory}
								failureReason={receive.failureReason}
							/>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
}

export const MemorizedPreviousReceiveHeader = React.memo(PreviousReceiveHeader);
